import { Clock, UserCheck, Lightbulb } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function AboutUs() {
    const features = [
        {
            title: "Reliability",
            description: "We guarantee punctual pickups and drop-offs with meticulously maintained vehicles.",
            icon: <Clock className="w-10 h-10 text-indigo-600" />
        },
        {
            title: "Professionalism",
            description: "Our chauffeurs undergo rigorous training in etiquette and customer service.",
            icon: <UserCheck className="w-10 h-10 text-indigo-600" />
        },
        {
            title: "Innovation",
            description: "We continuously upgrade our fleet and technology for superior service.",
            icon: <Lightbulb className="w-10 h-10 text-indigo-600" />
        }
    ];

    const vehicles = [
        {
            title: 'Swift Dezire',
            imgSrc: '/about/swift-dezire.png'
        },
        {
            title: 'Toyota Innova',
            imgSrc: '/about/toyota-innova.png'
        },
        {
            title: 'Mahindra Marazzo',
            imgSrc: '/about/mahindra-marazzo.png'
        },
        {
            title: 'Toyota Corolla',
            imgSrc: '/about/toyota-corolla.png'
        },
        {
            title: 'Ertiga',
            imgSrc: '/about/Ertiga_Car.png'
        },
        {
            title: 'Etios',
            imgSrc: '/about/etios.png'
        },
        {
            title: 'Innova Hycross',
            imgSrc: '/about/InnovaHycrossCar.png'
        },
        {
            title: 'Buses',
            imgSrc: '/about/buses.png'
        }
    ];

    return (
        <>
            <Helmet>
                <title>About PVR CABS | Trusted Employee Transport Company in Hyderabad</title>
                <meta name="description" content="Learn more about PVR CABS – Hyderabad’s leading employee and corporate transport partner since 2007, serving 200+ clients." />
                <link rel="canonical" href="https://pvrcabs.com/about" />
            </Helmet>

            <div className="">
                {/* Hero Section */}
                <section className="relative py-10 bg-gray-50 mt-4  text-black overflow-hidden">
                    <div className="text-center  ">
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-1 md:mb-6">
                            About <span className="text-green-400">PVR CABS</span>
                        </h1>
                        <p className="text-sm md:text-xl font-medium leading-relaxed px-4">
                            Pioneering transportation excellence in Hyderabad since 2007
                        </p>
                    </div>
                </section>

                {/* Company Story */}
                <section className="py-10">
                    <div className="w-full md:max-w-7xl mx-auto px-0 md:px-6">
                        <div className="flex flex-col lg:flex-row gap-12 items-center">
                            <div className="lg:w-1/2">
                                <div className="relative rounded-xl overflow-hidden shadow-lg h-80">
                                    <div className="absolute inset-0 bg-blue-900 opacity-30"></div>
                                    <img
                                        src="/Gemini_Generated_Image_yk998myk998myk99.png"
                                        alt="PVR CABS Fleet"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="lg:w-1/2">
                                <h2 className="text-xl md:text-xl lg:text-2xl font-bold text-gray-800 mb-3 md:mb-6">
                                    Our <span className="text-blue-600">Journey</span>
                                </h2>
                                <p className="text-gray-600 mb-4 leading-relaxed">
                                    PVR CABS began in 2007 with a vision to revolutionize corporate transportation in Hyderabad. Starting with just a handful of vehicles, we've grown into a fleet of over 600 cars serving Telangana's business community.
                                </p>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Today, we're recognized as one of Hyderabad's most reliable transport services, combining professional chauffeurs with meticulously maintained vehicles to deliver exceptional experiences.
                                </p>
                                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                                    <p className="text-blue-800 font-medium">
                                        "From modest beginnings to market leaders, our growth reflects our commitment to excellence in every journey."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="py-10 bg-blue-900 text-white">
                    <div className="w-full mx-auto px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            <div className="p-4">
                                <div className="text-xl md:text-3xl font-bold mb-2 text-yellow-400">15+</div>
                                <div className="text-sm md:text-base">Years in Service</div>
                            </div>
                            <div className="p-4">
                                <div className="text-xl md:text-3xl font-bold mb-2 text-yellow-400">600+</div>
                                <div className="text-sm md:text-base">Vehicles in Fleet</div>
                            </div>
                            <div className="p-4">
                                <div className="text-xl md:text-3xl font-bold mb-2 text-yellow-400">1000+</div>
                                <div className="text-sm md:text-base">Trained Chauffeurs</div>
                            </div>
                            <div className="p-4">
                                <div className="text-xl md:text-3xl font-bold mb-2 text-yellow-400">24/7</div>
                                <div className="text-sm md:text-base">Service Availability</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Fleet Section */}
                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-8 md:mb-12">
                            <h2 className="text-xl md:text-xl lg:text-2xl font-bold text-gray-800 mb-1 md:mb-4">
                                Our <span className="text-blue-600">Premium Fleet</span>
                            </h2>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                We maintain a diverse selection of vehicles to meet every corporate and leisure need
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {vehicles.map((vehicle, index) => (
                                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                                    <div className="h-32 bg-[#F4F4F4] flex items-center justify-center">
                                        <img src={vehicle.imgSrc} width="100%" className='w-full h-full object-contain' />
                                    </div>
                                    <div className="p-2">
                                        <h3 className="font-semibold text-sm md:text-lg text-gray-800 text-center">{vehicle.title}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Values Section */}
                <section className="py-16 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center">
                            <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                                Our <span className="text-blue-600">Core Values</span>
                            </h2>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                The principles that drive every decision we make
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 py-6 px-4 md:px-8">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100"
                                >
                                    <div className="flex items-center justify-center w-10 p-2 h-10 bg-indigo-100 rounded-xl mb-4">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Mission/Vision */}
                <section className="py-16 bg-gradient-to-r from-blue-800 to-blue-600 text-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid md:grid-cols-2 gap-12">
                            <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                                <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center">
                                    <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                                    </svg>
                                    Our Vision
                                </h2>
                                <p className="mb-4 leading-relaxed">
                                    To redefine corporate transportation by combining luxury, efficiency, and technology to create seamless travel experiences.
                                </p>
                                <p className="text-blue-100">
                                    We envision being the preferred mobility partner for India's corporate sector, setting industry benchmarks for service quality.
                                </p>
                            </div>
                            <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                                <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center">
                                    <svg className="w-6 h-6 mr-3 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                    </svg>
                                    Our Mission
                                </h2>
                                <p className="mb-4 leading-relaxed">
                                    To deliver safe, comfortable, and reliable transportation solutions that exceed client expectations through innovation and exceptional service.
                                </p>
                                <p className="text-blue-100">
                                    We're committed to sustainable growth while maintaining the personal touch that has defined our brand since 2007.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-6">
                            <h2 className="text-xl md:text-xl lg:text-2xl font-bold text-gray-800 mb-4">
                                Why Choose <span className="text-blue-600">PVR CABS</span>
                            </h2>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                The PVR difference goes beyond just transportation
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="flex items-start mb-4">
                                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base md:text-lg font-semibold text-gray-800">Safety First</h3>
                                        <p className="text-gray-600 mt-2">
                                            All vehicles undergo daily safety checks and regular maintenance. Our chauffeurs are trained in defensive driving and emergency protocols.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <div className="flex items-start mb-4">
                                    <div className="bg-green-100 p-3 rounded-full mr-4">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base md:text-lg font-semibold text-gray-800">Technology Driven</h3>
                                        <p className="text-gray-600 mt-2">
                                            Real-time tracking, automated billing, and digital documentation ensure transparency and convenience for all our corporate clients.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16 bg-gray-900 text-white">
                    <div className="max-w-7xl mx-auto px-6 text-center">
                        <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-6">Ready to Experience the PVR Difference?</h2>
                        <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
                            Join hundreds of satisfied corporate clients who trust us for their transportation needs.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <Link to="/quote" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-3 px-8 rounded-lg transition-colors">
                                Get a Quote
                            </Link>
                            <Link to="/contact" className="border border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-8 rounded-lg transition-colors">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    )
}